import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { FiSearch, FiX, FiPlus, FiChevronDown } from "react-icons/fi";
import { LuBuilding2 } from "react-icons/lu";
import { debounce } from "lodash";
import Cookies from "universal-cookie";
import { COMPANY_ID, COMPANYNAME, COMPANY_EMPLOYEE_ID, REDIRECT_URL, ROLE } from "../../config";
import { setCookies } from "../../utility";
import useSetDefaultCompany from "../../hooks/company/useSetDefaultCompany";
import useGetUserCompanies from "../../hooks/company/useGetUserCompanies";

const cookies = new Cookies();

const CompanySwitcherContent = ({
  companies = [],
  selectedAndDefault = [],
  selectedCompany = null,
  defaultCompany = null,
  isSameSelectedAndDefault = false,
  isLoading = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage = () => {},
  onCreateCompany,
  onClose,
  defaultAccordionOpen = false,
}) => {
  const activeCid = cookies.get(COMPANY_ID);
  const userRole = cookies.get(ROLE);
  const isCompanyAdmin = userRole === "COMPANY_ADMIN";
  const [isAccordionOpen, setIsAccordionOpen] = useState(defaultAccordionOpen || !activeCid);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const { handleSetDefaultCompany, isSettingDefault, targetCompanyId } =
    useSetDefaultCompany();

  const debouncedSearch = useRef(
    debounce((value) => {
      setSearchQuery(value);
    }, 300)
  ).current;

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  // Paginated search query via API
  const {
    companies: searchResults,
    isLoading: isSearchLoading,
    hasNextPage: searchHasNextPage,
    isFetchingNextPage: searchIsFetchingNextPage,
    fetchNextPage: searchFetchNextPage,
  } = useGetUserCompanies({
    enabled: Boolean(searchQuery),
    params: { search_query: searchQuery },
  });

  // Active pagination targets
  const activeHasNextPage = searchQuery ? searchHasNextPage : hasNextPage;
  const activeIsFetchingNextPage = searchQuery ? searchIsFetchingNextPage : isFetchingNextPage;
  const activeFetchNextPage = searchQuery ? searchFetchNextPage : fetchNextPage;

  // Resolve selected & default companies (prefer props from useGetUserCompanies, fallback to selectedAndDefault)
  const resolvedSelected =
    selectedCompany || selectedAndDefault.find((c) => c.is_selected === true) || null;

  const resolvedDefault =
    defaultCompany || selectedAndDefault.find((c) => c.default_company === true) || null;

  const isSame =
    isSameSelectedAndDefault ||
    (resolvedSelected &&
      resolvedDefault &&
      String(resolvedSelected.company_id) === String(resolvedDefault.company_id));

  // Determine if user has any options to switch or create company
  const hasSwitchOptions =
    companies.length > 0 || (!isSame && Boolean(resolvedDefault)) || isCompanyAdmin;

  // Switch company handler
  const handleSwitchCompany = (company) => {
    setCookies({
      [COMPANY_ID]: company.company_id,
      [COMPANYNAME]: company.company_name,
      [COMPANY_EMPLOYEE_ID]: company.company_employee_id || "",
    });
    window.location.href = "/";
  };

  // Create company handler
  const handleCreateCompany = () => {
    if (onCreateCompany) {
      onCreateCompany();
      return;
    }
    const appBaseUrl = REDIRECT_URL ? REDIRECT_URL.replace(/\/login.*$/, "") : "";
    window.location.href = `${appBaseUrl}/company/on-board`;
  };

  // Observer callback for infinite scrolling
  const paginationRef = useRef({
    hasNextPage: activeHasNextPage,
    isFetchingNextPage: activeIsFetchingNextPage,
    fetchNextPage: activeFetchNextPage,
  });
  useEffect(() => {
    paginationRef.current = {
      hasNextPage: activeHasNextPage,
      isFetchingNextPage: activeIsFetchingNextPage,
      fetchNextPage: activeFetchNextPage,
    };
  }, [activeHasNextPage, activeIsFetchingNextPage, activeFetchNextPage]);

  const observerRef = useRef(null);
  const sentinelRef = useCallback(
    (node) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (node) {
        observerRef.current = new IntersectionObserver(
          ([entry]) => {
            if (
              entry.isIntersecting &&
              paginationRef.current.hasNextPage &&
              !paginationRef.current.isFetchingNextPage
            ) {
              paginationRef.current.fetchNextPage();
            }
          },
          { threshold: 0.1 }
        );
        observerRef.current.observe(node);
      }
    },
    []
  );

  return (
    <div className="flex flex-col w-full text-gray-800">
      {/* Top Header with Close Button */}
      {onClose && (
        <div className="flex items-center justify-end px-3.5 pt-3 pb-1">
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition cursor-pointer"
            aria-label="Close"
          >
            <FiX className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Main Container */}
      <div className={`px-3.5 pb-2 ${onClose ? "" : "pt-3.5"}`}>
        {/* Selected Account Card (Accordion only when hasSwitchOptions) */}
        <div className="rounded-2xl border border-gray-200/90 bg-gray-50/90 overflow-hidden transition shadow-xs">
          <div
            onClick={() => {
              if (hasSwitchOptions) {
                setIsAccordionOpen(!isAccordionOpen);
              }
            }}
            className={`p-3.5 flex items-center justify-between gap-3 select-none transition ${
              hasSwitchOptions ? "cursor-pointer hover:bg-gray-100/70" : "cursor-default"
            }`}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-full bg-blue-100/90 text-blue-700 border border-blue-200/80 flex items-center justify-center shrink-0">
                <LuBuilding2 className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p
                  className="text-sm font-semibold text-gray-900 truncate leading-snug"
                  title={resolvedSelected?.company_name || "Select Company"}
                >
                  {resolvedSelected?.company_name || "Select Company"}
                </p>
                <p className="text-xs text-gray-500 truncate mt-0.5">
                  {[resolvedSelected?.industry, resolvedSelected?.city]
                    .filter(Boolean)
                    .join(" • ") || "Active Workspace"}
                </p>
              </div>
            </div>

            {/* Right: Default badge / Set Default action + Chevron Toggle (if multiple/admin) */}
            <div className="flex items-center gap-2 shrink-0">
              {isSame ? (
                <span className="px-2 py-0.5 text-[10px] font-semibold text-blue-700 bg-blue-100/80 border border-blue-200 rounded-full shrink-0">
                  Default
                </span>
              ) : (
                resolvedSelected?.company_id && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSetDefaultCompany(resolvedSelected.company_id);
                    }}
                    disabled={
                      isSettingDefault &&
                      String(targetCompanyId) === String(resolvedSelected.company_id)
                    }
                    className="px-2 py-0.5 text-[11px] font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 border border-blue-200 rounded-md transition cursor-pointer shrink-0 bg-white"
                    title="Set as Default"
                  >
                    {isSettingDefault &&
                    String(targetCompanyId) === String(resolvedSelected.company_id)
                      ? "..."
                      : "Set Default"}
                  </button>
                )
              )}

              {/* Accordion Chevron Toggle - only shown when there are switch/create options */}
              {hasSwitchOptions && (
                <div className="w-7 h-7 rounded-full bg-white border border-gray-200/80 flex items-center justify-center text-gray-500 hover:text-gray-800 shrink-0 shadow-xs transition-colors">
                  <FiChevronDown
                    className={`w-4 h-4 transition-transform duration-300 ease-in-out ${
                      isAccordionOpen ? "rotate-180 text-gray-800" : "rotate-0 text-gray-500"
                    }`}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Accordion Expanded Content (Only when hasSwitchOptions) */}
          {hasSwitchOptions && (
            <div
              className={`grid transition-all duration-300 ease-in-out ${
                isAccordionOpen
                  ? "grid-rows-[1fr] opacity-100"
                  : "grid-rows-[0fr] opacity-0"
              }`}
            >
            <div className="overflow-hidden">
              <div className="border-t border-gray-200/80 bg-white p-2.5">
                {/* Optional Search bar if multiple companies */}
                {(companies.length > 3 || searchQuery) && (
                  <div className="relative mb-2">
                    <FiSearch className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={handleSearch}
                      placeholder="Search other companies..."
                      className="w-full pl-8 pr-7 py-2 text-xs bg-gray-50 border border-gray-200/90 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white transition"
                    />
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearchTerm("");
                          setSearchQuery("");
                        }}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 cursor-pointer"
                      >
                        <FiX className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}

                {/* Company List Scroll Area */}
                <div className="max-h-60 overflow-y-auto space-y-1 pr-0.5">
                  {/* Search Results Mode */}
                  {searchQuery ? (
                    isSearchLoading ? (
                      <div className="flex justify-center items-center py-6">
                        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : searchResults.length === 0 ? (
                      <p className="py-4 text-xs text-gray-400 text-center">
                        No companies match "{searchTerm}"
                      </p>
                    ) : (
                      searchResults.map((company, idx) => {
                        const isActive =
                          String(activeCid) === String(company.company_id);
                        const isDefault = Boolean(company.default_company);
                        const isCurrentSetting =
                          isSettingDefault &&
                          String(targetCompanyId) === String(company.company_id);

                        return (
                          <div
                            key={`search-${company.company_id}-${idx}`}
                            onClick={() => handleSwitchCompany(company)}
                            className="px-3 py-2 rounded-xl hover:bg-gray-100 flex items-center justify-between gap-3 cursor-pointer transition group"
                          >
                            <div className="min-w-0 flex-1 pr-2">
                              <p
                                className="text-xs font-medium text-gray-800 group-hover:text-blue-600 truncate"
                                title={company.company_name}
                              >
                                {company.company_name}
                              </p>
                              {(company.industry || company.city) && (
                                <p className="text-[11px] text-gray-400 truncate mt-0.5">
                                  {[company.industry, company.city]
                                    .filter(Boolean)
                                    .join(" • ")}
                                </p>
                              )}
                            </div>

                            <div className="flex items-center gap-2 shrink-0">
                              {isActive && (
                                <span className="px-2 py-0.5 text-[10px] font-semibold text-blue-700 bg-blue-50 rounded-full border border-blue-200">
                                  Active
                                </span>
                              )}
                              {isDefault ? (
                                <span className="px-2 py-0.5 text-[10px] font-semibold text-blue-700 bg-blue-100/80 rounded-full border border-blue-200">
                                  Default
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSetDefaultCompany(company.company_id);
                                  }}
                                  disabled={isCurrentSetting}
                                  className="px-2 py-0.5 text-[11px] text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition cursor-pointer"
                                  title="Set as Default"
                                >
                                  {isCurrentSetting ? "..." : "Set Default"}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )
                  ) : (
                    /* Ordered View */
                    <>
                      {/* 1. Default Company (if different from selected) */}
                      {!isSame && resolvedDefault && (
                        <div
                          onClick={() => handleSwitchCompany(resolvedDefault)}
                          className="px-3 py-2 rounded-xl hover:bg-gray-100 flex items-center justify-between gap-3 cursor-pointer transition group"
                        >
                          <div className="min-w-0 flex-1 pr-2">
                            <p
                              className="text-xs font-medium text-gray-800 group-hover:text-blue-600 truncate"
                              title={resolvedDefault.company_name}
                            >
                              {resolvedDefault.company_name}
                            </p>
                            <p className="text-[11px] text-gray-400 truncate mt-0.5">
                              {[resolvedDefault.industry, resolvedDefault.city]
                                .filter(Boolean)
                                .join(" • ")}
                            </p>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <span className="px-2 py-0.5 text-[10px] font-semibold text-blue-700 bg-blue-100/80 rounded-full border border-blue-200">
                              Default
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Separator between default and other companies */}
                      {!isSame && resolvedDefault && companies.length > 0 && (
                        <hr className="my-1 border-gray-100 mx-1" />
                      )}

                      {/* 2. Other Companies */}
                      {companies.map((company, idx) => {
                        const isDefault = Boolean(company.default_company);
                        const isCurrentSetting =
                          isSettingDefault &&
                          String(targetCompanyId) === String(company.company_id);

                        return (
                          <div
                            key={`other-${company.company_id}-${idx}`}
                            onClick={() => handleSwitchCompany(company)}
                            className="px-3 py-2 rounded-xl hover:bg-gray-100 flex items-center justify-between gap-3 cursor-pointer transition group"
                          >
                            <div className="min-w-0 flex-1 pr-2">
                              <p
                                className="text-xs font-medium text-gray-800 group-hover:text-blue-600 truncate"
                                title={company.company_name}
                              >
                                {company.company_name}
                              </p>
                              {(company.industry || company.city) && (
                                <p className="text-[11px] text-gray-400 truncate mt-0.5">
                                  {[company.industry, company.city]
                                    .filter(Boolean)
                                    .join(" • ")}
                                </p>
                              )}
                            </div>

                            <div className="flex items-center gap-2 shrink-0">
                              {isDefault ? (
                                <span className="px-2 py-0.5 text-[10px] font-semibold text-blue-700 bg-blue-100/80 rounded-full border border-blue-200">
                                  Default
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSetDefaultCompany(company.company_id);
                                  }}
                                  disabled={isCurrentSetting}
                                  className="px-2 py-0.5 text-[11px] text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition cursor-pointer"
                                  title="Set as Default"
                                >
                                  {isCurrentSetting ? "..." : "Set Default"}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}

                    </>
                  )}

                  {/* Infinite scroll sentinel */}
                  <div ref={sentinelRef} className="h-1 w-full" />

                  {activeIsFetchingNextPage && (
                    <div className="flex justify-center items-center py-2">
                      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>

                {/* Add / Create Company Button (Company Admin only) */}
                {isCompanyAdmin && (
                  <div className="pt-2 mt-1.5 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={handleCreateCompany}
                      className="w-full px-3 py-2.5 text-xs text-gray-700 hover:bg-gray-100 rounded-xl flex items-center gap-2.5 transition cursor-pointer font-medium"
                    >
                      <div className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center text-gray-600">
                        <FiPlus className="w-3.5 h-3.5" />
                      </div>
                      <span>Create another company</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanySwitcherContent;
